/**
 * Settings — Soglia "stale estimate" (giorni dopo i quali un preventivo
 * inviato senza risposta viene segnalato nella dashboard come stale).
 *
 * Persistito su organizations.stale_days_threshold.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

const StaleThresholdCard: React.FC = () => {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();
  const [value, setValue] = useState<number>(30);

  const { data: current = 30, isLoading } = useQuery({
    queryKey: ['org-stale-threshold', currentOrganizationId],
    queryFn: async (): Promise<number> => {
      if (!currentOrganizationId) return 30;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('organizations')
        .select('stale_days_threshold')
        .eq('id', currentOrganizationId)
        .maybeSingle();
      if (error || !data) return 30;
      return Number(data.stale_days_threshold ?? 30);
    },
    enabled: !!currentOrganizationId,
  });

  useEffect(() => {
    setValue(current);
  }, [current]);

  const saveMutation = useMutation({
    mutationFn: async (newValue: number) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');
      if (newValue < 1 || newValue > 365) throw new Error('La soglia deve essere tra 1 e 365 giorni');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('organizations')
        .update({ stale_days_threshold: newValue })
        .eq('id', currentOrganizationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-stale-threshold', currentOrganizationId] });
      // Forza re-fetch della dashboard per usare la nuova soglia
      queryClient.invalidateQueries({ queryKey: ['dashboard-estimates-comprehensive'] });
      toast.success('Soglia aggiornata');
    },
    onError: (e: Error) => {
      toast.error(e.message || 'Errore nel salvataggio');
    },
  });

  const isDirty = value !== current;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Soglia "preventivo senza risposta"
        </CardTitle>
        <CardDescription>
          Dopo quanti giorni dall'invio un preventivo viene marcato come "stale" nella dashboard.
          Serve per ricordarti di richiamare i clienti che non hanno ancora risposto.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="stale-threshold">Giorni</Label>
            <Input
              id="stale-threshold"
              type="number"
              min={1}
              max={365}
              value={value}
              onChange={(e) => setValue(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
              disabled={isLoading}
            />
          </div>
          <Button
            onClick={() => saveMutation.mutate(value)}
            disabled={!isDirty || saveMutation.isPending || isLoading}
          >
            {saveMutation.isPending ? 'Salvataggio…' : 'Salva'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Valore corrente: <span className="font-medium">{current} giorni</span>. Default consigliato: 30.
        </p>
      </CardContent>
    </Card>
  );
};

export default StaleThresholdCard;
